/**
 * Where the reseller is, as a download surface.
 *
 * Statsboard needs every download attributed to the place it was triggered from, and that is
 * only knowable in the browser at the moment of the click. This is the single place that
 * answers it, so a new download control gets attribution by calling one function rather than
 * by someone remembering to thread a string.
 *
 * ⚠️ Read from the GRID'S LIVE STATE — the `#lm_filters` hidden input — not from the URL.
 * That input holds what the content loader asked the server for, so it already distinguishes
 * a collection from an artist page from a search, including the cases a path cannot, like a
 * search run from the front page. `ui/contentLoader.ts` both reads and WRITES it (infinite
 * scroll replaces its value), so it stays correct as the reader pages.
 *
 * ⚠️⚠️ This used to read `state/loaderState.ts`, and that is why attribution never worked.
 * That module is a typed signal holding exactly this state — but **nothing ever calls
 * `initLoaderState`**, so `getLoader()` returned `null` on every page and every cart and
 * quick download was recorded with no surface at all. It looked like a plumbing gap in the
 * reports for weeks, and was only pinned down once the `unattributed` bucket separated "we
 * never captured this" from "a wired control could not name its surface". The signal is a
 * half-finished refactor away from the hidden inputs; until something seeds it, the inputs
 * are the only source of truth. Do not switch back without wiring `initLoaderState` first.
 */
import { getDomElement } from "../utils/dom";
import type { DownloadSource } from "@brave-new-world/shared";

export interface DownloadSurface {
  source: DownloadSource;
  /**
   * Omitted rather than null when the surface points at nothing.
   *
   * This object is spread straight into the tRPC input, and `exactOptionalPropertyTypes`
   * makes "absent" and "present but null" different types at that boundary. Omitting is also
   * what the wire wants: a key that is not there is not sent.
   */
  sourceRef?: string;
}

/** The Bestsellers page's loader path, as `views/pages/Bestsellers.tsx` seeds it. */
const BESTSELLERS_PATH = "/bestsellers";

/**
 * The artwork this page load opened directly, if it opened one.
 *
 * ⚠️ An artwork id, not a boolean. A pasted `/view/123` renders the FRONT PAGE behind the
 * modal (`views/routing/modalRoute.tsx`), so that page has a grid like any other and
 * "no grid" cannot detect a direct link. What can is the modal the URL itself opened — and
 * keying it to that artwork means the flag expires by construction: close the modal, browse
 * the grid underneath and take something else, and that download is attributed to the grid
 * rather than inheriting "direct" from how the session happened to start.
 */
let bootArtworkId: string | null = null;

/**
 * Record the modal the URL opened at boot. Called by `pageInit` for `data-init-modal`.
 *
 * The key is the modal's dispatch string — `view:123` for an artwork — so anything that is
 * not an artwork view leaves this null and changes nothing.
 */
export function noteBootModal(key: string | null): void {
  const m = /^view:(\d+)/.exec(key ?? "");
  bootArtworkId = m?.[1] ?? null;
}

/** Tests only. */
export function resetBootModal(): void {
  bootArtworkId = null;
}

/**
 * The four filter keys this reads. `unknown` values because the JSON is whatever the server
 * put in the input, and a filter can legitimately be a string or a number.
 */
interface GridFilters {
  q?: unknown;
  lightbox_id?: unknown;
  collection_id?: unknown;
  artist_id?: unknown;
}

/**
 * A filter value as a non-empty string, or null.
 *
 * Narrowed to string/number first: anything else in that JSON is not an id we can use, and
 * stringifying it would put `[object Object]` in a `source_ref` column.
 */
function str(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const s = String(value).trim();
  return s === "" || s === "0" ? null : s;
}

/**
 * The grid's live filters, or null when this page has no grid to attribute to.
 *
 * ⚠️ Null and `{}` are different answers. `{}` is the front page — a real surface, and the
 * commonest one. Null means there is no listing here at all, or its filters would not
 * parse, and the caller records that as a fault rather than guessing `browse`.
 */
function gridFilters(): GridFilters | null {
  const el = getDomElement<HTMLInputElement>("lm_filters");
  if (!el) return null;
  try {
    const raw: unknown = JSON.parse(el.value || "{}");
    // No assertion needed: every field of `GridFilters` is optional, so a narrowed
    // `object` already satisfies it, and the four reads below each narrow their own value.
    return typeof raw === "object" && raw !== null ? raw : {};
  } catch {
    return null;
  }
}

/**
 * The surface a download should be attributed to.
 *
 * ⚠️ Search outranks the container it was run in. Searching "forest" inside a collection is
 * attributed to the search, because the search is the act that surfaced the artwork — the
 * collection merely bounded it. Crediting the collection would overstate curation, which is
 * the specific thing the attribution exists to measure honestly.
 *
 * `artworkId` is the artwork being taken, used only to recognise the direct-link case. A
 * bulk add has no single artwork and simply omits it.
 */
export function currentDownloadSurface(artworkId?: string | number): DownloadSurface {
  // Arrived straight at this artwork's viewer from a pasted link: there was no listing in
  // between, whatever page is rendered behind the modal.
  if (bootArtworkId !== null && String(artworkId ?? "") === bootArtworkId) {
    return { source: "direct" };
  }

  const filters = gridFilters();
  if (!filters) {
    // ⚠️ `unattributed`, not `unknown`. This is a wired download control that asked for its
    // surface and got nothing. That is a bug to go and fix, and reporting it as `unknown`
    // would bury it in the legacy bucket nobody chases — see `shared/downloadSource.ts`.
    return { source: "unattributed" };
  }

  const q = str(filters.q);
  if (q !== null) return { source: "search", sourceRef: q };

  const list = str(filters.lightbox_id);
  if (list !== null) return { source: "list", sourceRef: list };

  const collection = str(filters.collection_id);
  if (collection !== null) return { source: "collection", sourceRef: collection };

  const artist = str(filters.artist_id);
  if (artist !== null) return { source: "artist_profile", sourceRef: artist };

  // Below the four named containers, above `browse`: Bestsellers is a plain grid with no
  // filter of its own to recognise, so only the loader path tells it apart. A search run
  // from it still reports `search`, by the rule above.
  if ((getDomElement<HTMLInputElement>("lm_url")?.value ?? "") === BESTSELLERS_PATH) {
    return { source: "bestsellers" };
  }

  // Everything that is not one of the named surfaces is `browse`. That is a real answer,
  // not a fallback: the main grid is where most downloads come from.
  return { source: "browse" };
}

/**
 * The same, as query parameters for the plain-GET download route.
 *
 * `/ptdlfile` is a navigation, not a fetch, so its surface has to travel in the URL.
 */
export function downloadSurfaceQuery(artworkId?: string | number): string {
  const { source, sourceRef } = currentDownloadSurface(artworkId);
  const ref =
    sourceRef === undefined || sourceRef === "" ? "" : `&sref=${encodeURIComponent(sourceRef)}`;
  return `&src=${encodeURIComponent(source)}${ref}`;
}
